import { useState, useEffect, useRef, useCallback, startTransition } from 'react';
import { TripSheetProps } from '../../../datastore/tripSheets';
import { SSCCProps } from '../../../datastore/ssccs';
import { ASNOccurrences, ManifestOccurrences, SSCCOccurrences } from '../utils/types';

interface DuplicateDetectionResult {
  manifestOccurrences: ManifestOccurrences;
  ssccOccurrences: SSCCOccurrences;
  asnOccurrences: ASNOccurrences;
  isCalculatingManifest: boolean;
  isCalculatingSSCC: boolean;
  isCalculatingASN: boolean;
  hasCalculated: boolean;
}

interface DataSnapshot {
  minimalTrips: any[];
  minimalSsccs: any[];
}

const MIN_DEBOUNCE_MS = 250;
const MAX_DEBOUNCE_MS = 1200;
const MAX_RETRY_ATTEMPTS = 3;

function resolveAdaptiveDebounceMs(tripCount: number, ssccCount: number, lastTriggerAtMs: number): number {
  const size = tripCount + ssccCount;

  let debounceMs = MIN_DEBOUNCE_MS;
  if (size > 1500) {
    debounceMs = 900;
  } else if (size > 800) {
    debounceMs = 650;
  } else if (size > 300) {
    debounceMs = 450;
  }

  // If updates are arriving in bursts, expand debounce to coalesce more work.
  const nowMs = Date.now();
  if (lastTriggerAtMs > 0 && nowMs - lastTriggerAtMs < 300) {
    debounceMs += 250;
  }

  return Math.min(MAX_DEBOUNCE_MS, debounceMs);
}

function extractMinimalTrips(tripSheets: TripSheetProps[]) {
  return tripSheets.map((trip) => ({
    id: trip.id,
    reference: trip.reference,
    legs: (trip.legs || []).map((leg) => ({
      id: leg.id,
      manifests: (leg.manifests || []).map((manifest) => ({
        id: manifest.id,
        trailerId: manifest.trailerId,
        status: manifest.status,
        ssccs: (manifest.ssccs || []).map((sscc) => ({
          ssccId: sscc.ssccId,
          receiptedEvents: (sscc.receiptedEvents || []).map((event) => ({
            name: event.name,
            site: event.site,
            packages: (event.packages || []).map((pkg) => ({
              asn: pkg?.asn || null,
              id: pkg?.id || null,
              po: pkg?.po || null,
              status: pkg?.status || null,
              priority: pkg?.priority || null
            }))
          }))
        }))
      }))
    }))
  }));
}

function extractMinimalSsccs(ssccs: SSCCProps[]) {
  return (ssccs || []).map((sscc) => ({
    id: sscc.id,
    receiptedEvents: (sscc.receiptedEvents || []).map((event) => ({
      name: event.name,
      site: event.site,
      packages: (event.packages || []).map((pkg) => ({
        asn: pkg?.asn || null,
        id: pkg?.id || null,
        po: pkg?.po || null,
        status: pkg?.status || null,
        priority: pkg?.priority || null
      }))
    }))
  }));
}

/**
 * Hook that offloads duplicate detection to a Web Worker in three sequential steps:
 * manifest → SSCC → ASN. Automatically re-runs (debounced) whenever tripSheets changes.
 */
export function useDuplicateDetection(tripSheets: TripSheetProps[], ssccs: SSCCProps[]): DuplicateDetectionResult {
  const [manifestOccurrences, setManifestOccurrences] = useState<ManifestOccurrences>(new Map());
  const [ssccOccurrences, setSSCCOccurrences] = useState<SSCCOccurrences>(new Map());
  const [asnOccurrences, setASNOccurrences] = useState<ASNOccurrences>(new Map());
  const [isCalculatingManifest, setIsCalculatingManifest] = useState(false);
  const [isCalculatingSSCC, setIsCalculatingSSCC] = useState(false);
  const [isCalculatingASN, setIsCalculatingASN] = useState(false);
  const [hasCalculated, setHasCalculated] = useState(false);

  const workerRef = useRef<Worker | null>(null);
  const tripSheetsRef = useRef(tripSheets);
  const ssccsRef = useRef(ssccs);
  const snapshotRef = useRef<DataSnapshot>({ minimalTrips: [], minimalSsccs: [] });
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const activeRequestIdRef = useRef(0);
  const isInFlightRef = useRef(false);
  const rerunRequestedRef = useRef(false);
  const lastTriggerAtMsRef = useRef(0);
  const retryAttemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const resetRetryState = useCallback(() => {
    retryAttemptRef.current = 0;
    clearRetryTimer();
  }, [clearRetryTimer]);

  const startCalculation = useCallback(() => {
    if (!workerRef.current) {
      return;
    }

    if (isInFlightRef.current) {
      rerunRequestedRef.current = true;
      return;
    }

    const minimalTrips = extractMinimalTrips(tripSheetsRef.current);
    const minimalSsccs = extractMinimalSsccs(ssccsRef.current);
    snapshotRef.current = { minimalTrips, minimalSsccs };

    const requestId = ++requestIdRef.current;
    activeRequestIdRef.current = requestId;
    isInFlightRef.current = true;
    rerunRequestedRef.current = false;
    clearRetryTimer();

    setIsCalculatingManifest(true);
    setIsCalculatingSSCC(true);
    setIsCalculatingASN(true);

    workerRef.current.postMessage({
      step: 'manifest',
      requestId,
      tripSheets: minimalTrips
    });
  }, [clearRetryTimer]);

  const runCalc = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const debounceMs = resolveAdaptiveDebounceMs(
      tripSheetsRef.current.length,
      ssccsRef.current.length,
      lastTriggerAtMsRef.current
    );
    lastTriggerAtMsRef.current = Date.now();

    debounceTimerRef.current = setTimeout(() => {
      startCalculation();
    }, debounceMs);
  }, [startCalculation]);

  // Create worker on mount, wire up sequential step orchestration, terminate on unmount
  useEffect(() => {
    let worker: Worker;
    try {
      worker = new Worker(new URL('../workers/duplicateWorker.ts', import.meta.url));

      worker.onmessage = (e: MessageEvent) => {
        const { step, requestId } = e.data as {
          step: 'manifest' | 'sscc' | 'asn';
          requestId?: number;
        };

        if (!requestId || requestId !== activeRequestIdRef.current) {
          return;
        }

        if (step === 'manifest') {
          startTransition(() => {
            setManifestOccurrences(new Map(e.data.manifestOccurrences));
          });
          setIsCalculatingManifest(false);
          workerRef.current?.postMessage({
            step: 'sscc',
            requestId,
            tripSheets: snapshotRef.current.minimalTrips
          });
        } else if (step === 'sscc') {
          startTransition(() => {
            setSSCCOccurrences(new Map(e.data.ssccOccurrences));
          });
          setIsCalculatingSSCC(false);
          const ssccIds = (e.data.ssccOccurrences as [string, any][]).map(([key]) => key);
          workerRef.current?.postMessage({
            step: 'asn',
            requestId,
            tripSheets: snapshotRef.current.minimalTrips,
            ssccs: snapshotRef.current.minimalSsccs,
            ssccIds
          });
        } else if (step === 'asn') {
          startTransition(() => {
            setASNOccurrences(new Map(e.data.asnOccurrences));
          });
          setIsCalculatingASN(false);
          setHasCalculated(true);
          isInFlightRef.current = false;
          resetRetryState();

          if (rerunRequestedRef.current) {
            rerunRequestedRef.current = false;
            startCalculation();
          }
        }
      };

      worker.onerror = (err) => {
        console.error('Duplicate detection worker error:', err);
        setIsCalculatingManifest(false);
        setIsCalculatingSSCC(false);
        setIsCalculatingASN(false);
        isInFlightRef.current = false;

        if (retryAttemptRef.current < MAX_RETRY_ATTEMPTS) {
          retryAttemptRef.current += 1;
          const backoffMs = Math.min(500 * 2 ** (retryAttemptRef.current - 1), 3000);
          clearRetryTimer();
          retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            startCalculation();
          }, backoffMs);
          return;
        }

        clearRetryTimer();
      };

      workerRef.current = worker;
    } catch (err) {
      console.error('Failed to create duplicate detection worker:', err);
    }

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      clearRetryTimer();
      worker?.terminate();
      workerRef.current = null;
    };
  }, [clearRetryTimer, resetRetryState, startCalculation]);

  // Auto-run whenever tripSheets or ssccs changes (debounced inside runCalc)
  useEffect(() => {
    tripSheetsRef.current = tripSheets;
    ssccsRef.current = ssccs;
    resetRetryState();

    if (tripSheets.length > 0 || ssccs.length > 0) {
      runCalc();
    }
  }, [tripSheets, ssccs, resetRetryState, runCalc]);

  return {
    manifestOccurrences,
    ssccOccurrences,
    asnOccurrences,
    isCalculatingManifest,
    isCalculatingSSCC,
    isCalculatingASN,
    hasCalculated
  };
}
